/**
 * Feature Flags Hook
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  featureFlagService,
  CreateFeatureFlagDto,
  UpdateFeatureFlagDto,
} from '../services/featureFlagService';

const QUERY_KEYS = {
  flags: ['platform', 'feature-flags'],
  flag: (key: string) => ['platform', 'feature-flags', key],
  overrides: (key: string) => ['platform', 'feature-flags', key, 'overrides'],
  tenantFlags: (tenantId: string) => ['platform', 'tenant-flags', tenantId],
};

export function useFeatureFlags() {
  return useQuery({
    queryKey: QUERY_KEYS.flags,
    queryFn: featureFlagService.getAll,
  });
}

export function useFeatureFlag(key: string) {
  return useQuery({
    queryKey: QUERY_KEYS.flag(key),
    queryFn: () => featureFlagService.getByKey(key),
    enabled: !!key,
  });
}

export function useFlagOverrides(key: string) {
  return useQuery({
    queryKey: QUERY_KEYS.overrides(key),
    queryFn: () => featureFlagService.getOverrides(key),
    enabled: !!key,
  });
}

export function useTenantFlags(tenantId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.tenantFlags(tenantId),
    queryFn: () => featureFlagService.getTenantFlags(tenantId),
    enabled: !!tenantId,
  });
}

export function useCreateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateFeatureFlagDto) => featureFlagService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.flags });
    },
  });
}

export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, dto }: { key: string; dto: UpdateFeatureFlagDto }) =>
      featureFlagService.update(key, dto),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.flags });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.flag(key) });
    },
  });
}

export function useDeleteFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => featureFlagService.delete(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.flags });
    },
  });
}

export function useSetTenantOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      tenantId,
      value,
    }: {
      key: string;
      tenantId: string;
      value: boolean;
    }) => featureFlagService.setTenantOverride(key, tenantId, value),
    onSuccess: (_, { key, tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.flags });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.overrides(key) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tenantFlags(tenantId) });
    },
  });
}

export function useRemoveTenantOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, tenantId }: { key: string; tenantId: string }) =>
      featureFlagService.removeTenantOverride(key, tenantId),
    onSuccess: (_, { key, tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.flags });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.overrides(key) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tenantFlags(tenantId) });
    },
  });
}

export function useBulkSetTenantOverrides() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      overrides,
    }: {
      tenantId: string;
      overrides: Array<{ key: string; value: boolean }>;
    }) => featureFlagService.bulkSetTenantOverrides(tenantId, overrides),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.flags });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tenantFlags(tenantId) });
    },
  });
}

export function useBulkSetFlagOverrides() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      tenantIds,
      value,
    }: {
      key: string;
      tenantIds: string[];
      value: boolean;
    }) => featureFlagService.bulkSetFlagOverrides(key, tenantIds, value),
    onSuccess: (_, { key, tenantIds }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.flags });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.overrides(key) });
      tenantIds.forEach((tenantId) => {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.tenantFlags(tenantId),
        });
      });
    },
  });
}
